import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Plus, Search, Phone, Mail, Linkedin, MessageSquare, Calendar as CalIcon, ClipboardCheck, Trash2, Archive, RotateCcw, Upload, FileText, X, AlertTriangle, Download } from 'lucide-react';
import ExportLeadsMenu, { download } from '../components/leads/ExportLeadsMenu';
import ImportLeadsDialog from '../components/leads/ImportLeadsDialog';
import { SocialMediaEditor, SocialLinks } from '../components/SocialMediaEditor';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { RichTextEditor, HtmlView } from '../components/RichTextEditor';
import { EmailViewDialog } from '../components/leads/EmailViewDialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  STATUS_LABELS, STATUS_COLORS, fmtCurrency, fmtDate, fmtDateTime,
  PARTNERSHIP_TYPES, PARTNERSHIP_TYPE_LABELS, CLOSURE_TYPES, TIERS_BY_TYPE, ROLE_LABELS, CONTACT_TYPES,
} from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const errText = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg || '').filter(Boolean).join('; ') || fallback;
  return fallback;
};

const ACT_ICONS = {
  email: Mail, call: Phone, linkedin: Linkedin, meeting: CalIcon, note: MessageSquare,
  followup: ClipboardCheck, task: ClipboardCheck,
};

const FOLLOWUP_TYPES = [
  'Email', 'Phone Call', 'Discovery Meeting', 'Virtual Meeting',
  'In-Person Meeting', 'Proposal Follow-Up', 'Contract Review',
  'Partnership Check-In', 'Other',
];

const EMPTY_LEAD = {
  company_name: '', primary_contact: '', title: '', contact_type: '', email: '', phone: '',
  office_phone: '', mobile_phone: '',
  address_line1: '', address_line2: '', address_city: '', address_state: '', address_zip: '', address_county: '', address_country: '',
  mailing_address_line1: '', mailing_city: '', mailing_state: '', mailing_zip: '',
  social_media: [],
  website: '',
  industry: '', lead_source: '', campaign: '', assigned_to: '',
  partnership_type: 'media_partnership', partnership_tier: '',
  estimated_revenue: 0, actual_closed_value: 0, package_interest: '',
  status: 'new_lead', closure_type: 'not_closed', notes: '', revenue: 0, commission: 0,
  next_followup_date: '', next_followup_time: '', next_followup_type: 'Email',
  next_followup_priority: 'medium', next_followup_notes: '',
};

const TYPE_BADGE = {
  media_partnership: 'bg-magenta/10 text-magenta',
  strategic_partnership: 'bg-navy/10 text-navy',
  sponsorship: 'bg-amber-100 text-amber-700',
  marketplace_partner: 'bg-cyan-100 text-cyan-700',
  workforce_partner: 'bg-emerald-100 text-emerald-700',
  gala_partner: 'bg-purple-100 text-purple-700',
  cec: 'bg-teal-100 text-teal-700',
  other: 'bg-slate-100 text-slate-700',
};

export default function LeadsPage() {
  const { user: me, isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [letter, setLetter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [newLead, setNewLead] = useState(EMPTY_LEAD);
  const [selected, setSelected] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actType, setActType] = useState('note');
  const [actContent, setActContent] = useState('');
  const [completing, setCompleting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const [dupReason, setDupReason] = useState('');
  const [emailView, setEmailView] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [completeForm, setCompleteForm] = useState({
    notes: '', activity_type: 'call', new_status: '',
    next_followup_date: '', next_followup_time: '', next_followup_type: 'Email',
    next_followup_priority: 'medium', next_followup_notes: '',
  });

  const load = async () => {
    const r = await api.get(`/leads${showArchived ? '?include_archived=true' : ''}`);
    setLeads(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);
  useEffect(() => { api.get('/users').then((r) => setUsers(r.data)); }, []);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      api.get(`/leads/${id}`).then((r) => openDetail(r.data)).catch(() => toast.error('Lead not found'));
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const filtered = leads.filter((l) => {
    if (!showArchived && l.archived) return false;
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (typeFilter !== 'all' && l.partnership_type !== typeFilter) return false;
    if (letter !== 'all') {
      const lt = letter.toLowerCase();
      if (!(l.company_name || '').trim().toLowerCase().startsWith(lt) && !(l.primary_contact || '').trim().toLowerCase().startsWith(lt)) return false;
    }
    if (q) {
      const s = q.toLowerCase();
      return (l.company_name + ' ' + l.primary_contact + ' ' + (l.email || '')).toLowerCase().includes(s);
    }
    return true;
  }).sort((a, b) => {
    const low = (v) => (v || '').trim().toLowerCase();
    const stageOrder = Object.keys(STATUS_LABELS);
    switch (sortBy) {
      case 'name_asc': return low(a.primary_contact).localeCompare(low(b.primary_contact));
      case 'name_desc': return low(b.primary_contact).localeCompare(low(a.primary_contact));
      case 'company_asc': return low(a.company_name).localeCompare(low(b.company_name));
      case 'company_desc': return low(b.company_name).localeCompare(low(a.company_name));
      case 'created_desc': return (b.created_at || '').localeCompare(a.created_at || '');
      case 'created_asc': return (a.created_at || '').localeCompare(b.created_at || '');
      case 'contacted_desc': return (b.last_contact_date || '').localeCompare(a.last_contact_date || '');
      case 'tier': return (low(a.partnership_tier) || 'zzzz').localeCompare(low(b.partnership_tier) || 'zzzz');
      case 'stage': return stageOrder.indexOf(a.status) - stageOrder.indexOf(b.status);
      case 'updated_desc':
      default: return (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || '');
    }
  });

  const createLead = async () => {
    const payload = {
      ...newLead,
      email: newLead.email?.trim() || null,
      estimated_revenue: parseFloat(newLead.estimated_revenue) || 0,
      actual_closed_value: parseFloat(newLead.actual_closed_value) || 0,
      revenue: parseFloat(newLead.revenue) || 0,
      commission: parseFloat(newLead.commission) || 0,
    };
    try {
      await api.post('/leads', payload);
      toast.success('Lead created');
      setOpen(false);
      setNewLead(EMPTY_LEAD);
      load();
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.detail?.matches) {
        setDupWarning({ matches: e.response.data.detail.matches, payload });
      } else {
        toast.error(errText(e, 'Failed to create lead'));
      }
    }
  };

  const createAnyway = async () => {
    if (!dupReason.trim()) return toast.error('A reason is required to override');
    try {
      await api.post('/leads', { ...dupWarning.payload, duplicate_override_reason: dupReason });
      toast.success('Lead created (duplicate override logged)');
      setDupWarning(null); setDupReason(''); setOpen(false); setNewLead(EMPTY_LEAD); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Failed'); }
  };

  const mergeIntoExisting = async (matchId) => {
    try {
      await api.post(`/leads/${matchId}/merge-data`, dupWarning.payload);
      toast.success('Your entry was merged into the existing record');
      setDupWarning(null); setOpen(false); setNewLead(EMPTY_LEAD); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Merge failed'); }
  };

  const openDetail = async (lead) => {
    setSelected(lead);
    const [acts, docs] = await Promise.all([
      api.get(`/leads/${lead.id}/activities`),
      api.get(`/leads/${lead.id}/documents`),
    ]);
    setActivities(acts.data);
    setDocuments(docs.data);
  };

  const saveLead = async () => {
    const payload = { ...selected };
    delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.created_by;
    delete payload.archived; delete payload.archived_at; delete payload.archived_by;
    await api.put(`/leads/${selected.id}`, payload);
    toast.success('Lead updated');
    load();
  };

  const addActivity = async () => {
    if (!actContent.trim()) return;
    await api.post('/activities', { lead_id: selected.id, activity_type: actType, content: actContent });
    setActContent('');
    const r = await api.get(`/leads/${selected.id}/activities`);
    setActivities(r.data);
    toast.success('Activity logged');
  };

  const archive = async (lead) => {
    if (!confirm(`Archive ${lead.company_name}? It can be restored later.`)) return;
    await api.post(`/leads/${lead.id}/archive`);
    toast.success('Archived');
    setSelected(null);
    load();
  };

  const restore = async (lead) => {
    await api.post(`/leads/${lead.id}/restore`);
    toast.success('Restored');
    setSelected(null);
    load();
  };

  const remove = async (lead) => {
    setDeleteConfirm(false);
    await api.delete(`/leads/${lead.id}`);
    toast.success('Deleted');
    setSelected(null);
    load();
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('label', file.name);
      await api.post(`/leads/${selected.id}/documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      const r = await api.get(`/leads/${selected.id}/documents`);
      setDocuments(r.data);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (docId) => {
    if (!confirm('Delete this document?')) return;
    await api.delete(`/leads/${selected.id}/documents/${docId}`);
    toast.success('Deleted');
    const r = await api.get(`/leads/${selected.id}/documents`);
    setDocuments(r.data);
  };

  const completeFollowup = async () => {
    if (!completeForm.notes.trim()) return toast.error('Notes required');
    if (!completeForm.next_followup_date) {
      if (!confirm('No next follow-up scheduled. Save anyway? (Recommended: always schedule a next step)')) return;
    }
    try {
      const payload = { ...completeForm };
      if (!payload.new_status) delete payload.new_status;
      await api.post(`/leads/${selected.id}/complete-followup`, payload);
      toast.success('Follow-up completed');
      setCompleting(false);
      setCompleteForm({ notes: '', activity_type: 'call', new_status: '', next_followup_date: '', next_followup_time: '', next_followup_type: 'Email', next_followup_priority: 'medium', next_followup_notes: '' });
      // refresh
      openDetail({ id: selected.id });
      load();
      // Refresh selected from server
      const r = await api.get(`/leads/${selected.id}`);
      setSelected(r.data);
    } catch (e) {
      toast.error('Failed to complete follow-up');
    }
  };

  const tiers = TIERS_BY_TYPE[newLead.partnership_type] || [];
  const selectedTiers = selected ? (TIERS_BY_TYPE[selected.partnership_type] || []) : [];

  const renderFollowupFields = (state, setState) => (
    <>
      <div className="col-span-2 mt-2">
        <div className="text-[11px] uppercase tracking-wider font-bold text-magenta mb-2">Next Follow-Up</div>
      </div>
      <div>
        <Label className="text-xs">Date</Label>
        <Input
          type="date"
          value={state.next_followup_date ? state.next_followup_date.slice(0, 10) : ''}
          onChange={(e) => setState({ ...state, next_followup_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
          data-testid="followup-date"
        />
      </div>
      <div>
        <Label className="text-xs">Time</Label>
        <Input
          type="time"
          value={state.next_followup_time || ''}
          onChange={(e) => setState({ ...state, next_followup_time: e.target.value })}
          data-testid="followup-time"
        />
      </div>
      <div>
        <Label className="text-xs">Follow-Up Type</Label>
        <Select value={state.next_followup_type || ''} onValueChange={(v) => setState({ ...state, next_followup_type: v })}>
          <SelectTrigger data-testid="followup-type"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{FOLLOWUP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Priority</Label>
        <Select value={state.next_followup_priority || 'medium'} onValueChange={(v) => setState({ ...state, next_followup_priority: v })}>
          <SelectTrigger data-testid="followup-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Label className="text-xs">Follow-Up Notes</Label>
        <Textarea value={state.next_followup_notes || ''} onChange={(e) => setState({ ...state, next_followup_notes: e.target.value })} rows={2} data-testid="followup-notes" />
      </div>
    </>
  );

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex flex-1 gap-2 max-w-3xl flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search company, contact, email, phone, tier, stage, notes, tags..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="leads-search" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-52" data-testid="leads-type-filter"><SelectValue placeholder="Partnership Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Partnership Types</SelectItem>
              {PARTNERSHIP_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-52" data-testid="leads-sort-by"><SelectValue placeholder="Sort by" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="updated_desc">Sort: Last Updated</SelectItem>
              <SelectItem value="name_asc">Name (A–Z)</SelectItem>
              <SelectItem value="name_desc">Name (Z–A)</SelectItem>
              <SelectItem value="company_asc">Company (A–Z)</SelectItem>
              <SelectItem value="company_desc">Company (Z–A)</SelectItem>
              <SelectItem value="created_desc">Date Created (Newest)</SelectItem>
              <SelectItem value="created_asc">Date Created (Oldest)</SelectItem>
              <SelectItem value="contacted_desc">Recently Contacted</SelectItem>
              <SelectItem value="tier">Partnership Tier</SelectItem>
              <SelectItem value="stage">Partnership Stage</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button variant={showArchived ? 'default' : 'outline'} onClick={() => setShowArchived(!showArchived)} data-testid="toggle-archived">
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-leads-button">
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
          )}
          <ExportLeadsMenu
            isAdmin={isAdmin}
            currentFilters={{
              status: statusFilter !== 'all' ? statusFilter : '',
              partnership_type: typeFilter !== 'all' ? typeFilter : '',
              q,
              include_archived: showArchived ? 'true' : '',
            }}
            users={users}
          />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-magenta hover:bg-magenta/90" data-testid="new-lead-button"><Plus className="w-4 h-4 mr-2" /> New Lead</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Create New Partnership</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-2">
              {[
                { k: 'company_name', l: 'Company Name *' }, { k: 'primary_contact', l: 'Contact Name *' },
                { k: 'title', l: 'Contact Title' }, { k: 'email', l: 'Email', t: 'email' },
                { k: 'office_phone', l: 'Office Phone' }, { k: 'mobile_phone', l: 'Mobile Phone' },
                { k: 'phone', l: 'Other Phone' }, { k: 'website', l: 'Website' },
                { k: 'address_line1', l: 'Street Address 1' }, { k: 'address_line2', l: 'Street Address 2' },
                { k: 'address_city', l: 'City' }, { k: 'address_state', l: 'State' },
                { k: 'address_zip', l: 'ZIP/Postal Code' }, { k: 'address_county', l: 'County' },
                { k: 'address_country', l: 'Country' },
                { k: 'mailing_address_line1', l: 'Mailing Address / P.O. Box' }, { k: 'mailing_city', l: 'Mailing City' },
                { k: 'mailing_state', l: 'Mailing State' }, { k: 'mailing_zip', l: 'Mailing Zip' },
                { k: 'industry', l: 'Industry' }, { k: 'lead_source', l: 'Lead Source' },
                { k: 'campaign', l: 'Campaign' }, { k: 'estimated_revenue', l: 'Estimated Value', t: 'number' },
              ].map((f) => (
                <div key={f.k}>
                  <Label className="text-xs">{f.l}</Label>
                  <Input type={f.t || 'text'} value={newLead[f.k]} onChange={(e) => setNewLead({ ...newLead, [f.k]: e.target.value })} data-testid={`new-lead-${f.k}`} />
                </div>
              ))}
              <div>
                <Label className="text-xs">Contact Type</Label>
                <Select value={newLead.contact_type || undefined} onValueChange={(v) => setNewLead({ ...newLead, contact_type: v })}>
                  <SelectTrigger data-testid="new-lead-contact-type"><SelectValue placeholder="Select contact type" /></SelectTrigger>
                  <SelectContent>{CONTACT_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Partnership Type *</Label>
                <Select value={newLead.partnership_type} onValueChange={(v) => setNewLead({ ...newLead, partnership_type: v, partnership_tier: '' })}>
                  <SelectTrigger data-testid="new-lead-partnership-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTNERSHIP_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Partnership Tier</Label>
                <Select value={newLead.partnership_tier} onValueChange={(v) => setNewLead({ ...newLead, partnership_tier: v })}>
                  <SelectTrigger data-testid="new-lead-partnership-tier"><SelectValue placeholder="Select tier" /></SelectTrigger>
                  <SelectContent>{tiers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Assigned Team Member</Label>
                <Select value={newLead.assigned_to} onValueChange={(v) => setNewLead({ ...newLead, assigned_to: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={newLead.status} onValueChange={(v) => setNewLead({ ...newLead, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Closure Type</Label>
                <Select value={newLead.closure_type} onValueChange={(v) => setNewLead({ ...newLead, closure_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CLOSURE_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {renderFollowupFields(newLead, setNewLead)}
              <div className="col-span-2">
                <SocialMediaEditor value={newLead.social_media} onChange={(v) => setNewLead({ ...newLead, social_media: v })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes</Label>
                <RichTextEditor value={newLead.notes} onChange={(v) => setNewLead({ ...newLead, notes: v })} minHeight={120} label="Lead Notes" testId="new-lead-notes-editor" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={createLead} className="bg-magenta hover:bg-magenta/90" data-testid="save-new-lead">Create Lead</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Alphabetical filter */}
      <div className="flex flex-wrap gap-1" data-testid="alpha-filter">
        {['all', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'].map((lt) => (
          <button
            key={lt}
            onClick={() => setLetter(lt === letter ? 'all' : lt)}
            className={`min-w-[28px] h-7 px-1.5 rounded-md text-xs font-semibold transition ${
              letter === lt ? 'bg-magenta text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-magenta/50 hover:text-magenta'
            }`}
            data-testid={`alpha-${lt}`}
          >
            {lt === 'all' ? 'All' : lt}
          </button>
        ))}
        {filtered.length === 0 && <span className="text-xs text-slate-400 ml-2 self-center">No results found</span>}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-4 py-3">Company</th>
                <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-4 py-3">Contact</th>
                <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-4 py-3">Type</th>
                <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-4 py-3">Status</th>
                <th className="text-right text-xs font-heading uppercase tracking-wider text-slate-500 px-4 py-3">Est. Value</th>
                <th className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-4 py-3">Next Follow-Up</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} onClick={() => openDetail(l)} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${l.archived ? 'opacity-60 italic' : ''}`} data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-sm text-navy">{l.company_name}{l.archived && <span className="ml-2 text-[10px] text-rose-500 not-italic font-bold">ARCHIVED</span>}</div>
                    <div className="text-xs text-slate-500">{l.industry}</div>
                  </td>
                  <td className="px-4 py-3"><div className="text-sm">{l.primary_contact}</div><div className="text-xs text-slate-500">{l.title}</div></td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${TYPE_BADGE[l.partnership_type] || TYPE_BADGE.other}`}>{PARTNERSHIP_TYPE_LABELS[l.partnership_type] || '—'}</span></td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${STATUS_COLORS[l.status]}`}>{STATUS_LABELS[l.status]}</span></td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-navy">{fmtCurrency(l.estimated_revenue)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(l.next_followup_date)}{l.next_followup_time ? ` ${l.next_followup_time}` : ''}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-slate-500 text-sm">No leads match your filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lead Detail Sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="lead-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-navy font-heading text-2xl flex items-center gap-2 flex-wrap">
                  {selected.company_name}
                  {selected.archived && <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">ARCHIVED</span>}
                </SheetTitle>
                <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                  <span>{selected.primary_contact}</span>
                  {selected.title && <span>· {selected.title}</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${TYPE_BADGE[selected.partnership_type] || TYPE_BADGE.other}`}>{PARTNERSHIP_TYPE_LABELS[selected.partnership_type] || 'Other'}</span>
                </div>
                <SocialLinks links={selected.social_media} />
                {isAdmin && (
                  <div className="flex gap-2 pt-2 flex-wrap">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" data-testid="lead-export-single"><Download className="w-3.5 h-3.5 mr-1.5" />Export</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {['xlsx', 'csv', 'pdf'].map((f) => (
                          <DropdownMenuItem key={f} onClick={() => download(f, { ids: selected.id })} data-testid={`lead-export-${f}`}>
                            {f === 'xlsx' ? 'Excel (.xlsx)' : f.toUpperCase()}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {selected.archived ? (
                      <Button variant="outline" size="sm" onClick={() => restore(selected)} data-testid="lead-restore"><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Restore</Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => archive(selected)} data-testid="lead-archive"><Archive className="w-3.5 h-3.5 mr-1.5" />Archive</Button>
                    )}
                    <Button variant="outline" size="sm" className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setDeleteConfirm(true)} data-testid="lead-delete"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete</Button>
                  </div>
                )}
              </SheetHeader>

              <Tabs defaultValue="details" className="mt-6">
                <TabsList>
                  <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                  <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline ({activities.length})</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="tab-documents">Documents ({documents.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="details" className="space-y-3 mt-4">
                  {selected.next_followup_date && !selected.archived && (
                    <div className="bg-magenta/5 border border-magenta/20 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="next-followup-banner">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-magenta">Next Follow-Up</div>
                        <div className="text-sm font-semibold text-navy mt-0.5">{fmtDate(selected.next_followup_date)} {selected.next_followup_time} · {selected.next_followup_type || '—'}</div>
                        <div className="text-xs text-slate-500">{selected.next_followup_notes}</div>
                      </div>
                      <Button onClick={() => setCompleting(true)} className="bg-magenta hover:bg-magenta/90" size="sm" data-testid="complete-followup-button">Complete Follow-Up</Button>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { k: 'email', l: 'Email' }, { k: 'office_phone', l: 'Office Phone' }, { k: 'mobile_phone', l: 'Mobile Phone' },
                      { k: 'phone', l: 'Other Phone' }, { k: 'website', l: 'Website' },
                      { k: 'address_line1', l: 'Street Address 1' }, { k: 'address_line2', l: 'Street Address 2' },
                      { k: 'address_city', l: 'City' }, { k: 'address_state', l: 'State' },
                      { k: 'address_zip', l: 'ZIP/Postal Code' }, { k: 'address_county', l: 'County' },
                      { k: 'address_country', l: 'Country' },
                      { k: 'mailing_address_line1', l: 'Mailing Address / P.O. Box' }, { k: 'mailing_city', l: 'Mailing City' },
                      { k: 'mailing_state', l: 'Mailing State' }, { k: 'mailing_zip', l: 'Mailing Zip' },
                      { k: 'industry', l: 'Industry' }, { k: 'lead_source', l: 'Lead Source' }, { k: 'campaign', l: 'Campaign' },
                      { k: 'estimated_revenue', l: 'Estimated Value', t: 'number' }, { k: 'actual_closed_value', l: 'Actual Closed Value', t: 'number' },
                      { k: 'revenue', l: 'Revenue', t: 'number' }, { k: 'commission', l: 'Commission', t: 'number' },
                    ].map((f) => (
                      <div key={f.k}>
                        <Label className="text-xs">{f.l}</Label>
                        <Input type={f.t || 'text'} value={selected[f.k] || ''} onChange={(e) => setSelected({ ...selected, [f.k]: e.target.value })} />
                      </div>
                    ))}
                    <div>
                      <Label className="text-xs">Contact Type</Label>
                      <Select value={selected.contact_type || undefined} onValueChange={(v) => setSelected({ ...selected, contact_type: v })}>
                        <SelectTrigger data-testid="lead-contact-type"><SelectValue placeholder="Select contact type" /></SelectTrigger>
                        <SelectContent>{CONTACT_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Partnership Type</Label>
                      <Select value={selected.partnership_type} onValueChange={(v) => setSelected({ ...selected, partnership_type: v, partnership_tier: '' })}>
                        <SelectTrigger data-testid="lead-partnership-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{PARTNERSHIP_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Partnership Tier</Label>
                      <Select value={selected.partnership_tier || ''} onValueChange={(v) => setSelected({ ...selected, partnership_tier: v })}>
                        <SelectTrigger><SelectValue placeholder="Select tier" /></SelectTrigger>
                        <SelectContent>{selectedTiers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={selected.status} onValueChange={(v) => setSelected({ ...selected, status: v })}>
                        <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Closure Type</Label>
                      <Select value={selected.closure_type || 'not_closed'} onValueChange={(v) => setSelected({ ...selected, closure_type: v })}>
                        <SelectTrigger data-testid="lead-closure-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{CLOSURE_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Assigned Team Member</Label>
                      <Select value={selected.assigned_to || ''} onValueChange={(v) => setSelected({ ...selected, assigned_to: v })} disabled={!isAdmin && selected.assigned_to !== me?.id}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {renderFollowupFields(selected, setSelected)}
                    <div className="col-span-2">
                      <SocialMediaEditor value={selected.social_media || []} onChange={(v) => setSelected({ ...selected, social_media: v })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Notes</Label>
                      <RichTextEditor value={selected.notes || ''} onChange={(v) => setSelected({ ...selected, notes: v })} minHeight={160} label={`Notes — ${selected.company_name || ''}`} testId="lead-notes-editor" />
                    </div>
                  </div>
                  <Button onClick={saveLead} className="bg-magenta hover:bg-magenta/90 w-full" data-testid="save-lead">Save Changes</Button>
                </TabsContent>

                <TabsContent value="timeline" className="mt-4">
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2 mb-4">
                    <div className="flex gap-2">
                      <Select value={actType} onValueChange={setActType}>
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['email', 'call', 'linkedin', 'meeting', 'note', 'followup'].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input placeholder="What happened?" value={actContent} onChange={(e) => setActContent(e.target.value)} data-testid="activity-input" />
                      <Button onClick={addActivity} className="bg-navy" data-testid="log-activity">Log</Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {activities.map((a) => {
                      const Icon = ACT_ICONS[a.activity_type] || MessageSquare;
                      return (
                        <div key={a.id} className="flex gap-3 border-b border-slate-100 pb-3 last:border-0">
                          <div className="w-9 h-9 rounded-full bg-magenta/10 text-magenta flex items-center justify-center shrink-0">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                              <span className="text-sm font-semibold text-navy">{a.user_name || 'System'}</span>
                              {a.user_role && (
                                <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded uppercase tracking-wider font-bold">{ROLE_LABELS[a.user_role] || a.user_role}</span>
                              )}
                              <span className="text-[10px] text-slate-400 ml-auto">{fmtDateTime(a.created_at)}</span>
                            </div>
                            <div className="text-xs text-magenta font-semibold uppercase tracking-wider mb-0.5">{(a.activity_type || '').replace('_', ' ')}</div>
                            <div className="text-sm text-slate-700"><HtmlView html={a.content} testId={`activity-content-${a.id}`} /></div>
                            {a.campaign_id && (
                              <button
                                type="button"
                                onClick={() => setEmailView({ campaignId: a.campaign_id, email: selected?.email })}
                                className="text-[11px] font-semibold text-magenta hover:underline mt-1"
                                data-testid={`view-email-${a.id}`}
                              >
                                View full email
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {activities.length === 0 && <div className="text-center text-sm text-slate-500 py-8">No activities yet</div>}
                  </div>
                  {emailView && <EmailViewDialog campaignId={emailView.campaignId} recipientEmail={emailView.email} onClose={() => setEmailView(null)} />}
                </TabsContent>

                <TabsContent value="documents" className="mt-4">
                  <div className="bg-slate-50 rounded-xl p-4 mb-4">
                    <Label className="text-xs font-semibold text-slate-700">Upload Document</Label>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        type="file"
                        onChange={(e) => e.target.files?.[0] && uploadDoc(e.target.files[0])}
                        disabled={uploading}
                        data-testid="document-upload-input"
                      />
                      {uploading && <span className="text-xs text-slate-500">Uploading...</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-2">Contracts, media kits, proposals, signed agreements, etc.</div>
                  </div>

                  {documents.length === 0 ? (
                    <div className="text-center text-sm text-slate-500 py-8">No documents uploaded yet</div>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((d) => (
                        <div key={d.id} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3" data-testid={`document-${d.id}`}>
                          <div className="w-9 h-9 rounded-lg bg-magenta/10 text-magenta flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-navy truncate">{d.label || d.file_name}</div>
                            <div className="text-[11px] text-slate-500">Uploaded by {d.uploaded_by_name} · {fmtDateTime(d.created_at)}</div>
                          </div>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${d.file_path}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-semibold text-navy hover:text-magenta"
                            data-testid={`document-open-${d.id}`}
                          >
                            Open
                          </a>
                          {isAdmin && (
                            <button onClick={() => removeDoc(d.id)} className="text-rose-500 hover:text-rose-700" data-testid={`document-delete-${d.id}`}>
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Import Dialog */}
      <ImportLeadsDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />

      {/* Duplicate warning dialog */}
      <Dialog open={!!dupWarning} onOpenChange={(v) => { if (!v) { setDupWarning(null); setDupReason(''); } }}>
        <DialogContent className="max-w-lg" data-testid="duplicate-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-5 h-5" /> Potential Duplicate Found</DialogTitle>
            <DialogDescription>A CRM record with similar information already exists. Please review the matching record(s) before creating a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(dupWarning?.matches || []).map((m) => (
              <div key={m.id} className="border border-slate-200 rounded-xl p-3" data-testid={`dup-match-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm">
                    <div className="font-semibold text-navy">{m.company_name}</div>
                    <div className="text-xs text-slate-500">{m.primary_contact} · {m.email || 'no email'} · {m.phone || 'no phone'}</div>
                    <div className="text-xs text-slate-400">Status: {STATUS_LABELS[m.status] || m.status} · Owner: {m.assigned_to_name || users.find((u) => u.id === m.assigned_to)?.full_name || '—'} · Updated {fmtDate(m.updated_at)}</div>
                    <div className="text-[11px] text-amber-600 mt-1">Matched on: {m.reasons.join(', ')}</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={async () => { const r = await api.get(`/leads/${m.id}`); setDupWarning(null); setOpen(false); openDetail(r.data); }} data-testid={`dup-open-${m.id}`}>
                    Open Existing Record
                  </Button>
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => mergeIntoExisting(m.id)} data-testid={`dup-merge-${m.id}`}>
                      Merge Into This Record
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs">Override reason (required to create anyway — will be logged)</Label>
              <Input value={dupReason} onChange={(e) => setDupReason(e.target.value)} placeholder="Why is this a legitimate separate record?" data-testid="dup-override-reason" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDupWarning(null); setDupReason(''); }} data-testid="dup-cancel">Cancel</Button>
            {isAdmin && (
              <Button onClick={createAnyway} disabled={!dupReason.trim()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="dup-create-anyway">
                Create Anyway
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Follow-Up Dialog */}
      <Dialog open={completing} onOpenChange={setCompleting}>
        <DialogContent className="max-w-lg" data-testid="complete-followup-dialog">
          <DialogHeader><DialogTitle>Complete Follow-Up</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Activity Type</Label>
              <Select value={completeForm.activity_type} onValueChange={(v) => setCompleteForm({ ...completeForm, activity_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['call', 'email', 'meeting', 'linkedin', 'note', 'followup'].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes from this interaction *</Label>
              <div data-testid="complete-notes">
                <RichTextEditor
                  value={completeForm.notes}
                  onChange={(v) => setCompleteForm({ ...completeForm, notes: v })}
                  minHeight={110}
                  placeholder="What was discussed? Next steps?"
                  label="Interaction Notes"
                  testId="complete-notes-editor"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Update Status (optional)</Label>
              <Select value={completeForm.new_status} onValueChange={(v) => setCompleteForm({ ...completeForm, new_status: v })}>
                <SelectTrigger><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">(no change)</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-magenta pt-2">Schedule Next Follow-Up</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={completeForm.next_followup_date ? completeForm.next_followup_date.slice(0, 10) : ''} onChange={(e) => setCompleteForm({ ...completeForm, next_followup_date: e.target.value ? new Date(e.target.value).toISOString() : '' })} data-testid="next-followup-date" />
              </div>
              <div>
                <Label className="text-xs">Time</Label>
                <Input type="time" value={completeForm.next_followup_time} onChange={(e) => setCompleteForm({ ...completeForm, next_followup_time: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={completeForm.next_followup_type} onValueChange={(v) => setCompleteForm({ ...completeForm, next_followup_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FOLLOWUP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={completeForm.next_followup_priority} onValueChange={(v) => setCompleteForm({ ...completeForm, next_followup_priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Follow-Up Notes</Label>
              <Textarea value={completeForm.next_followup_notes} onChange={(e) => setCompleteForm({ ...completeForm, next_followup_notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleting(false)}>Cancel</Button>
            <Button onClick={completeFollowup} className="bg-magenta hover:bg-magenta/90" data-testid="save-complete-followup">Save & Schedule Next</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" /> Permanently delete this partnership?
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                You are about to permanently delete <strong>{selected.company_name}</strong>. This action cannot be undone.
              </p>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-800 space-y-2">
                <div className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> The following will be permanently erased:</div>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>All notes and details ({(selected.notes || '').length} characters in the Notes section)</li>
                  <li>{activities.length} timeline entr{activities.length === 1 ? 'y' : 'ies'}</li>
                  <li>{documents.length} uploaded document{documents.length === 1 ? '' : 's'}</li>
                  <li>All follow-up history and scheduled next steps</li>
                  <li>Revenue, commission, and closure data</li>
                </ul>
                <div className="text-xs pt-1">Consider <strong>Archiving</strong> instead — archived leads can be restored later.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
            {selected && (
              <>
                <Button variant="outline" onClick={() => { setDeleteConfirm(false); archive(selected); }}>
                  <Archive className="w-4 h-4 mr-2" /> Archive Instead
                </Button>
                <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => remove(selected)} data-testid="confirm-delete-lead">
                  <Trash2 className="w-4 h-4 mr-2" /> Yes, Delete Forever
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
